import { DropdownMenuItem } from "@front/m19-ui";
import { M19Environment } from "@front/m19-models/Environment";

export interface NavItem {
  icon?: string;
  label: string;
  id?: string;
  url?: string;
  href?: string;
  children?: NavItem[];
  isChild?: boolean;
  onlyBeta?: boolean;
  environment?: Extract<M19Environment, "dev" | "demo">;
}

export const dashboardMenu: NavItem = {
  label: "page-title.dashboard",
  children: [
    {
      icon: "icon-[lucide--home]",
      label: "page-title.home",
      url: "/dashboard/home",
      id: "dashboard-home",
    },
    {
      icon: "icon-[lucide--layout-dashboard]",
      label: "page-title.account_360",
      url: "/dashboard/account360",
      id: "dashboard-account360",
    },
    {
      icon: "icon-[lucide--line-chart]",
      label: "page-title.advertising",
      url: "/dashboard/advertising",
      id: "dashboard-advertising",
    },
    {
      icon: "icon-[lucide--dollar-sign]",
      label: "page-title.profit_and_loss",
      url: "/dashboard/profit-and-loss",
      id: "dashboard-profit-and-loss",
    },
  ],
};

export const advertisingMenu: NavItem = {
  label: "page-title.advertising",
  children: [
    {
      icon: "icon-[lucide--lightbulb]",
      label: "common.strategies",
      id: "strategies",
      children: [
        {
          label: "page-title.sp",
          url: "/advertising/sponsored-product",
          id: "sponsored-product",
        },
        {
          label: "page-title.sb",
          url: "/advertising/sponsored-brands",
          id: "sponsored-brands",
        },
        {
          label: "page-title.sd",
          url: "/advertising/sponsored-display",
          id: "sponsored-display",
        },
        {
          label: "page-title.dsp",
          url: "/advertising/dsp/strategies",
          id: "dsp-strategies",
          onlyBeta: true,
        },
        {
          label: "metrics.TACOS_title",
          url: "/advertising/tacos-strategies",
          id: "tacos",
        },
      ],
    },
    {
      label: "page-title.dsp",
      icon: "icon-[lucide--globe]",
      url: "/advertising/dsp",
      id: "dsp",
    },
    {
      label: "page-title.amc",
      icon: "icon-[lucide--cloud]",
      url: "/advertising/amc",
      id: "amc",
    },
    {
      label: "page-title.more",
      id: "advertising-more",
      icon: "icon-[lucide--ellipsis]",
      children: [
        {
          label: "page-title.hourly_stats",
          url: "/advertising/hourly-stats",
          id: "hourly-stats",
        },
        {
          label: "page-title.promo_days_boost",
          url: "/advertising/promo-days-boost",
          id: "promo-day",
        },
      ],
    },
  ],
};

export const productMenu: NavItem = {
  label: "page-title.product_center",
  children: [
    {
      label: "page-title.product_analysis",
      icon: "icon-[lucide--search]",
      url: "/product-center/product-analysis",
      id: "product-analysis",
    },
    {
      label: "page-title.catalog",
      icon: "icon-[lucide--book-open]",
      url: "/product-center/catalog",
      id: "catalog",
    },
    {
      label: "page-title.inventory",
      url: "/product-center/inventory",
      id: "inventory",
      icon: "icon-[lucide--box]",
    },
    {
      label: "page-title.more",
      id: "product-more",
      icon: "icon-[lucide--ellipsis]",
      children: [
        {
          label: "page-title.product_360",
          url: "/product-center/product360",
          id: "product-360",
        },
        {
          label: "page-title.product_tracker",
          url: "/product-center/product-tracker",
          id: "product-tracker",
        },

        {
          label: "page-title.product_groups",
          url: "/product-center/product-group",
          id: "product-group",
        },
        {
          label: "page-title.brand_alias",
          url: "/product-center/brand-alias",
          id: "brand-alias",
        },
      ],
    },
  ],
};

export const keywordMenu: NavItem = {
  label: "page-title.keyword_center",
  children: [
    {
      label: "page-title.traffic_analysis",
      icon: "icon-[lucide--mouse-pointer-click]",
      url: "/keyword-center/traffic-analysis",
      id: "traffic-analysis",
    },
    {
      label: "page-title.keyword_tracker",
      icon: "icon-[lucide--chart-no-axes-column]",
      url: "/keyword-center/keyword-tracker",
      id: "keyword-tracker",
    },
    {
      label: "page-title.more",
      id: "keyword-more",
      icon: "icon-[lucide--ellipsis]",
      children: [
        {
          label: "page-title.share_of_voice",
          url: "/keyword-center/share-of-voice",
          id: "share-of-voice",
        },
        {
          label: "page-title.search_trends",
          url: "/keyword-center/search-trends",
          id: "search-trends",
        },
        {
          label: "page-title.segments",
          url: "/keyword-center/segments",
          id: "segments",
        },
      ],
    },
  ],
};

export function getSettingsMenu(releaseNotesUrl: string, referralProgram?: string): DropdownMenuItem[][] {
  return [
    [
      {
        id: "profile",
        label: "page-title.my_profile",
        icon: "icon-[lucide--user]",
        url: "/profile",
      },
      {
        id: "accounts",
        label: "page-title.accounts",
        icon: "icon-[lucide--users]",
        url: "/accounts",
      },
      {
        id: "user-management",
        label: "page-title.user_management",
        icon: "icon-[lucide--user-cog]",
        url: "/user-management",
      },
      {
        id: "activities",
        label: "page-title.activities",
        icon: "icon-[lucide--newspaper]",
        url: "/activities",
      },
    ],
    [
      {
        id: "billing",
        label: "page-title.billing",
        icon: "icon-[lucide--credit-card]",
        url: "/billing",
      },
      {
        id: "data-hub",
        label: "page-title.data_hub",
        icon: "icon-[lucide--database]",
        url: "/datahub",
      },
      {
        id: "referral-program",
        label: "page-title.referral_program",
        icon: "icon-[lucide--gift]",
        href: referralProgram,
      },
      {
        id: "release-notes",
        label: "page-title.release_notes",
        icon: "icon-[lucide--sparkles]",
        href: releaseNotesUrl,
      },
    ],
  ];
}
